import { useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, Send, Link2, ChevronDown } from "lucide-react";
import { BrokerOffer } from "../types";
import { formatRubles } from "../utils/math_helpers";
import type { SnsBlock } from "../utils/useSnsFlow";

/**
 * Первый список ответа с подбором (#441): банки, подключённые к СНС, — заявку в них можно
 * завести и отправить прямо из диалога. Общий для «Диалога по заявке» и «AI агента».
 * Выбор банков — чекбоксами (#446): проходящие отмечены сразу, одна кнопка подаёт в
 * отмеченные (один — сценарий на банк, несколько — одна заявка СНС во все).
 */
const keyOf = (o: BrokerOffer) => `${o.bank}|${o.platform || ""}`;

export default function SnsOffersBlock({ block, onSubmit, onSubmitMany, disabled, title, collapsible }: {
  block?: SnsBlock;
  onSubmit: (o: BrokerOffer) => void;
  /** Подать в отмеченные банки; без него — кнопка «Подать в СНС» у каждого банка. */
  onSubmitMany?: (offers: BrokerOffer[]) => void;
  disabled?: boolean;
  title?: string;
  /** Свёрнутым предложением после текста ответа (в ответе с подбором); без него — открытый блок. */
  collapsible?: boolean;
}) {
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [sent, setSent] = useState(false);

  if (!block) return null;
  if (block.state === "loading") {
    return (
      <div className="mb-2 inline-flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Проверяю, какие банки подбора подключены к СНС…
      </div>
    );
  }
  if (block.state !== "ok") {
    return block.state === "off"
      ? <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">{block.note}</div>
      : null;   // временный сбой проверки — не мешаем основному ответу
  }
  if (!block.offers.length) {
    return <div className="mb-2 text-[11px] text-slate-500">Из банков подбора к СНС не подключён ни один — подача только напрямую на площадках.</div>;
  }

  const picker = !!onSubmitMany;
  // По умолчанию отмечены проходящие по условиям; выбор брокера — поверх.
  const sel = checked ?? new Set(block.offers.filter((o) => o.eligible).map(keyOf));
  const chosen = block.offers.filter((o) => sel.has(keyOf(o)));
  const toggle = (o: BrokerOffer) => {
    const n = new Set(sel);
    n.has(keyOf(o)) ? n.delete(keyOf(o)) : n.add(keyOf(o));
    setChecked(n);
  };
  const off = disabled || sent;

  const eligibleCount = block.offers.filter((o) => o.eligible).length;
  const list = (
    <>
      <div className="space-y-1.5">
        {block.offers.map((o) => (
          <label key={keyOf(o)}
            className={`flex items-center justify-between gap-2 bg-white border rounded-lg px-2.5 py-1.5 ${
              picker && sel.has(keyOf(o)) ? "border-indigo-300" : "border-slate-100"} ${picker && !off ? "cursor-pointer" : ""}`}>
            {picker && (
              <input type="checkbox" className="accent-indigo-600 w-4 h-4 shrink-0" disabled={off}
                checked={sel.has(keyOf(o))} onChange={() => toggle(o)} />
            )}
            <div className="min-w-0 flex-1 text-[12px]">
              <div className="font-bold text-slate-900 flex items-center gap-1.5 flex-wrap">
                {o.bank}
                {o.eligible
                  ? <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700"><CheckCircle2 className="w-3 h-3" /> проходит</span>
                  : <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700" title={(o.reasons || []).join("; ")}>
                      <AlertTriangle className="w-3 h-3" /> не проходит</span>}
              </div>
              <div className="text-[11px] text-slate-500">
                {o.commission != null
                  ? <>комиссия <b className="font-mono text-slate-800">{formatRubles(o.commission)}</b></>
                  : <>цену назовёт СНС</>}
                {o.avRate ? <> · КВ {Math.round(o.avRate * 100)}%</> : null}
                {o.platform ? <> · {o.platform}</> : null}
              </div>
            </div>
            {!picker && o.eligible && (
              <button onClick={() => onSubmit(o)} disabled={disabled}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-[10px] font-bold rounded-lg cursor-pointer">
                <Send className="w-3 h-3" /> Подать в СНС
              </button>
            )}
          </label>
        ))}
      </div>
      {picker && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <button disabled={off || !chosen.length}
            onClick={() => { setSent(true); chosen.length === 1 ? onSubmit(chosen[0]) : onSubmitMany!(chosen); }}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs font-bold rounded-lg cursor-pointer">
            <Send className="w-3.5 h-3.5" /> Подать в СНС{chosen.length ? `: выбрано ${chosen.length}` : ""}
          </button>
          {chosen.some((o) => !o.eligible) && !sent && (
            <span className="text-[11px] text-amber-700">среди отмеченных есть непроходящие по условиям — покажу причины перед подачей</span>
          )}
          {sent && <span className="text-[11px] text-slate-400">выбор отправлен — см. ниже</span>}
        </div>
      )}
    </>
  );
  if (collapsible) {
    // Свёрнутое предложение после текста ответа — как «Предложения банков».
    return (
      <details className="group/sns bg-indigo-50/40 border border-indigo-100 rounded-xl overflow-hidden">
        <summary className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer select-none text-xs font-bold text-indigo-800 hover:bg-indigo-50">
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <Send className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">Подать в СНС: {block.offers.length} {plural(block.offers.length, "банк подключён", "банка подключено", "банков подключено")} · {eligibleCount} проходят<span className="group-open/sns:hidden"> — раскрыть</span></span>
          </span>
          <ChevronDown className="w-4 h-4 text-indigo-400 transition-transform group-open/sns:rotate-180 shrink-0" />
        </summary>
        <div className="p-2.5 border-t border-indigo-100">{list}</div>
      </details>
    );
  }
  return (
    <div className="mb-3 border border-indigo-100 bg-indigo-50/40 rounded-xl p-2.5">
      <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-800 mb-1.5">
        <Link2 className="w-3.5 h-3.5" /> {title || "Через СНС — подача прямо отсюда"} ({block.offers.length})
      </div>
      {list}
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
